import React from 'react'
import styled from 'styled-components'
import Heading from 'Event/template/Cards/Dashboard/Sidebar/Heading'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {useEditMode} from 'Event/EditModeProvider'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import AddResourceButton from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/ResourceList/AddResourceButton'
import ResourceItem, {
  Resource,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/ResourceList/ResourceItem'
import Grid from '@material-ui/core/Grid'
import {useAttendeeVariables} from 'Event'

import {
  DragDropContext,
  Droppable,
  DroppableProvidedProps,
  DropResult,
} from 'react-beautiful-dnd'
import {useCardsTemplate} from 'Event/template/Cards'
import {useToggle} from 'lib/toggle'
import ResourceListConfig from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/ResourceList/ResourceListConfig'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import {useHasVisibleItems} from 'Event/attendee-rules/matcher'
import Section from 'Event/template/Cards/Dashboard/Sidebar/Section'
import {
  HasOffset,
  useEditSidebarItem,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem'
import {createPositions, HashMap, Ordered, orderedIdsByPosition} from 'lib/list'
import {HasRules} from 'Event/attendee-rules'
import {HasSchedule} from 'lib/ui/layout/Scheduled'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useRemoveTranslations} from 'Event/LanguageProvider'

export const RESOURCE_LIST = 'Resource List'
export interface ResourceListProps
  extends Ordered,
    HasRules,
    HasOffset,
    HasSchedule {
  type: typeof RESOURCE_LIST
  title: string
  description: string
  resources: HashMap<Resource>
  isEnabled: boolean
}

export const createResourceList = (): ResourceListProps => ({
  type: RESOURCE_LIST,
  title: 'Resources',
  description: '',
  resources: {},
  isEnabled: true,
  offsetTop: 0,
  offsetBottom: 0,
})

export const RESOURCE_ICON = {
  pdf: 'fas fa-file-pdf',
  attachment: 'fas fa-paperclip',
  people: 'fas fa-users',
  image: 'fas fa-image',
  photoLibrary: 'fas fa-images',
}

export function ResourceList(
  props: ResourceListProps & {
    id: string
  },
) {
  const isEdit = useEditMode()
  const {
    resources,
    description,
    title,
    isEnabled,
    offsetTop,
    offsetBottom,
  } = props
  const {sidebar} = useCardsTemplate()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()

  const v = useAttendeeVariables()

  const hasVisibleItems = useHasVisibleItems(Object.values(resources))

  const showing = hasVisibleItems && isEnabled
  if (!showing && !isEdit) {
    return null
  }

  return (
    <Section offsetTop={offsetTop} offsetBottom={offsetBottom}>
      <EditModeOnly>
        <ResourceListConfig
          id={props.id}
          showing={configVisible}
          onClose={toggleConfig}
          list={props}
        />
        <RemoveResourcesButton {...props} />
      </EditModeOnly>
      <Editable onEdit={toggleConfig}>
        <Heading aria-label="resources">{v(title)}</Heading>
      </Editable>
      <Description
        aria-label="resource description"
        textColor={sidebar.textColor}
      >
        {v(description)}
      </Description>
      <Content {...props} />
      <EditModeOnly>
        <StyledAddResourceButton list={props} listId={props.id} />
      </EditModeOnly>
    </Section>
  )
}

function RemoveResourcesButton(props: ResourceListProps & {id: string}) {
  const {id, resources} = props
  const saveTemplate = useSaveTemplate()
  const removeTranslation = useRemoveTranslations()

  const resourceIds = Object.keys(resources)

  const remove = () => {
    saveTemplate({
      sidebarItems: {
        [id]: REMOVE,
      },
      localization: removeTranslation([
        `${id}_title`,
        `${id}_description`,
        ...resourceIds.flatMap((id) => [
          `${id}_name`,
          `${id}_filePath`,
          `${id}_url`,
        ]),
      ]),
    })
  }

  return (
    <RemoveButton size="large" showing onClick={remove}>
      Remove Resources
    </RemoveButton>
  )
}

function Content(props: ResourceListProps & {id: string}) {
  const isEdit = useEditMode()
  if (isEdit) {
    return <DroppableList {...props} />
  }

  return (
    <Container>
      <Resources {...props} />
    </Container>
  )
}

function DroppableList(props: ResourceListProps & {id: string}) {
  const handleDrag = useHandleDrag(props)

  return (
    <DragDropContext onDragEnd={handleDrag}>
      <Droppable droppableId="drag-and-drop-resources">
        {(provided) => (
          <Container ref={provided.innerRef} {...provided.droppableProps}>
            <>
              <Resources {...props} />
              {provided.placeholder}
            </>
          </Container>
        )}
      </Droppable>
    </DragDropContext>
  )
}

function Resources(props: ResourceListProps & {id: string}) {
  const {sidebar} = useCardsTemplate()
  const ids = orderedIdsByPosition(props.resources)

  return (
    <>
      {ids.map((id: string, index: number) => {
        const resource = props.resources[id]
        return (
          <ResourceItem
            droppleid={`resource-item-${id}`}
            id={id}
            resource={resource}
            iconColor={sidebar.textColor}
            index={index}
            key={id}
            list={props}
            listId={props.id}
          />
        )
      })}
    </>
  )
}

const Container = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: React.ReactElement | React.ReactElement[]
  } & Partial<DroppableProvidedProps>
>((props, ref) => (
  <Box className={props.className} ref={ref} {...props}>
    <Grid container>{props.children}</Grid>
  </Box>
))

function useHandleDrag(props: ResourceListProps) {
  const {update} = useEditSidebarItem()

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const ids = orderedIdsByPosition(props.resources)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      resources: createPositions(ids),
    })
  }
}

const Box = styled.div`
  margin-bottom: 30px;
  width: 100%;
`

const StyledAddResourceButton = styled(AddResourceButton)`
  margin-bottom: ${(props) => props.theme.spacing[6]}!important;
  margin-top: ${(props) => props.theme.spacing[5]}!important;
`

const Description = styled.p<{textColor: string | null}>`
  color: ${(props) => props.color};
`
